import { Link, type LinkProps } from '@tanstack/react-router'
import type { VariantProps } from 'class-variance-authority'
import type { MouseEvent, ReactNode } from 'react'
import { Button, type buttonVariants } from '#components/button'

/**
 * A button component that renders as a link using TanStack Router's Link component.
 *
 * Combines the styling and behavior of the Button component with the navigation
 * capabilities of TanStack Router's Link. Supports all button variants and sizes
 * while maintaining proper accessibility attributes and disabled state handling.
 *
 * @example
 * <ButtonLink to="/dashboard">Go to Dashboard</ButtonLink>
 *
 * @example
 * <ButtonLink variant="secondary" size="lg" to="/settings">Settings</ButtonLink>
 *
 * @example
 * <ButtonLink to="/profile" disabled>Profile (Disabled)</ButtonLink>
 */
type ButtonLinkProps = Omit<LinkProps<'a'>, 'children'> &
	VariantProps<typeof buttonVariants> & {
		children: ReactNode
		className?: string
		disabled?: boolean
		onClick?: (event: MouseEvent<HTMLAnchorElement>) => void
		tabIndex?: number
	}

function ButtonLink({
	variant,
	children,
	className,
	disabled = false,
	size,
	...props
}: ButtonLinkProps) {
	return (
		<Button
			className={className}
			disabled={disabled}
			nativeButton={false}
			render={
				<Link
					{...props}
					aria-disabled={disabled || undefined}
					onClick={(event) => {
						if (disabled) {
							event.preventDefault()
							event.stopPropagation()
						} else {
							props.onClick?.(event)
						}
					}}
					tabIndex={disabled ? -1 : props.tabIndex}
				/>
			}
			size={size}
			variant={variant}
		>
			{children}
		</Button>
	)
}

export { ButtonLink }
